/**
 * Taken from https://semver.org/spec/v2.0.0.html.
 */
export const semanticVersionRegexEmbeddable = /(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?/;
/**
 * Taken from https://semver.org/spec/v2.0.0.html.
 */
export const semanticVersionRegex = new RegExp(`^${semanticVersionRegexEmbeddable.source}$`);
const numberRegex = /0|[1-9][0-9]*/;
export function semanticVersionRegexGroupsToVersion(groups) {
    const prereleaseMatch = groups[4];
    const buildMatch = groups[5];
    let prereleaseInfo = null;
    if (prereleaseMatch) {
        prereleaseInfo = new PreReleaseInfo(prereleaseMatch
            .split(".")
            .map((r) => (r.match(numberRegex) ? parseInt(r) : r)));
    }
    const buildInfo = buildMatch !== undefined ? new BuildInfo(buildMatch.split(".")) : null;
    return new SemanticVersion(parseInt(groups[1]), parseInt(groups[2]), parseInt(groups[3]), prereleaseInfo, buildInfo);
}
/**
 * Represents a valid semantic version.
 * See https://semver.org/spec/v2.0.0.html.
 */
export class SemanticVersion {
    major;
    minor;
    patch;
    prerelease;
    build;
    static parse(text) {
        const exec = semanticVersionRegex.exec(text);
        if (!exec) {
            throw new Error(`Could not parse semantic version. "${text}" is not valid.`);
        }
        return semanticVersionRegexGroupsToVersion(exec);
    }
    constructor(major, minor, patch, prerelease, build) {
        this.major = major;
        this.minor = minor;
        this.patch = patch;
        this.prerelease = prerelease;
        this.build = build;
        function testValidNumber(n, arg) {
            if (!Number.isInteger(n) || n < 0) {
                throw new Error(`Invalid input. "${n}" is not valid for ${arg}`);
            }
        }
        testValidNumber(major, "major");
        testValidNumber(minor, "minor");
        testValidNumber(patch, "patch");
    }
    get isStable() {
        return this.major > 0;
    }
    toString() {
        let result = `${this.major}.${this.minor}.${this.patch}`;
        if (this.prerelease) {
            result += `-${this.prerelease.toString()}`;
        }
        if (this.build) {
            result += `+${this.build.toString()}`;
        }
        return result;
    }
    equals(other) {
        return this.toString() === other.toString();
    }
    with(update) {
        function defaultIfUndefined(val, defaultVal) {
            return val !== undefined ? val : defaultVal;
        }
        function defaultIfUndefinedNumber(val, defaultVal) {
            if (val === "increment") {
                return defaultVal + 1;
            }
            return val !== undefined ? val : defaultVal;
        }
        return new SemanticVersion(defaultIfUndefinedNumber(update.major, this.major), defaultIfUndefinedNumber(update.minor, this.minor), defaultIfUndefinedNumber(update.patch, this.patch), defaultIfUndefined(update.prerelease, this.prerelease), defaultIfUndefined(update.build, this.build));
    }
    toJSON() {
        return {
            major: this.major,
            minor: this.minor,
            patch: this.patch,
            ...(this.prerelease ? { prerelease: this.prerelease.parts } : {}),
            ...(this.build ? { build: this.build.parts } : {}),
        };
    }
    /**
     * Returns -1 if this version is older than the other version, 0 if they are equal, or 1 if this version is newer.
     */
    compareTo(other) {
        if (this.major != other.major) {
            return comparePrimitives(this.major, other.major);
        }
        if (this.minor != other.minor) {
            return comparePrimitives(this.minor, other.minor);
        }
        if (this.patch != other.patch) {
            return comparePrimitives(this.patch, other.patch);
        }
        return PreReleaseInfo.compare(this.prerelease, other.prerelease);
    }
}
function comparePrimitives(a, b) {
    if (a < b) {
        return -1;
    }
    if (a > b) {
        return 1;
    }
    return 0;
}
export class PreReleaseInfo {
    parts;
    static compare(a, b) {
        if (!a && !b) {
            return 0;
        }
        if (!a) {
            // prefer non-prerelease version.
            return 1;
        }
        if (!b) {
            return -1;
        }
        return a.compareTo(b);
    }
    constructor(parts) {
        this.parts = parts;
        if (parts.length === 0) {
            throw new Error("Must have at least one part!");
        }
        for (const p of parts) {
            if (typeof p === "string") {
                if (!/^[0-9a-zA-Z-]*[a-zA-Z-][0-9a-zA-Z-]*$/.exec(p)) {
                    throw new Error("Invalid input! Non-number parts must have at least one non-digit!");
                }
            }
        }
    }
    toString() {
        return this.parts.map((p) => p.toString()).join(".");
    }
    isNewer(other) {
        return this.compareTo(other) === 1;
    }
    isOlder(other) {
        return this.compareTo(other) === -1;
    }
    compareTo(other) {
        for (let i = 0; i < Math.max(this.parts.length, other.parts.length); i++) {
            const a = this.parts[i];
            const b = other.parts[i];
            if (a === undefined) {
                // Other has longer prerelease
                return -1;
            }
            if (b === undefined) {
                // This has longer prerelease
                return 1;
            }
            if (a !== b) {
                return comparePrimitives(a, b);
            }
        }
        return 0;
    }
}
export class BuildInfo {
    parts;
    constructor(parts) {
        this.parts = parts;
        if (parts.length === 0) {
            throw new Error("Must have at least one part!");
        }
    }
    toString() {
        return this.parts.join(".");
    }
}
//# sourceMappingURL=index.js.map